import React, { useContext, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppDataProvider/AppDataProvider";
import { ActionType } from "../../AppDataProvider/actions";
import { Input, NavButtons } from "../../common/components";
import { InputElement } from "../../common/components";
import { marginBottom, marginTop, typography } from "../../common/styles";
import { requireBookingDataFields } from "../../utils";
import { BookingData } from "../../AppDataProvider/types";
import { PartRequired } from "../../utils/types";
type WaitlistStepBookingData = PartRequired<BookingData, "organisation" | "service" | "timezone">;
const JOIN_WAITLIST_MUTATION = gql`
  mutation JoinWaitlist($input: JoinWaitlistInput!) {
    joinWaitlist(input: $input) {
      success
      message
    }
  }
`;
type JoinWaitlistData = {
  joinWaitlist: {
    success: boolean;
    message: string | null;
  } | null;
};
type JoinWaitlistVariables = {
  input: {
    organisationId: string;
    serviceId: string;
    bookingId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    timezone: string;
  };
};
type Errors = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
};
export function WaitlistStep(): React.ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  requireBookingDataFields<WaitlistStepBookingData>(appData.bookingData, ["organisation", "service", "timezone"]);
  const {
    organisation,
    service,
    timezone
  } = appData.bookingData;
  const {
    waitlistBooking
  } = appData;
  const {
    isPhoneNumberRequiredForBookings
  } = organisation;
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [joinWaitlist, {
    loading
  }] = useMutation<JoinWaitlistData, JoinWaitlistVariables>(JOIN_WAITLIST_MUTATION);
  const clearError = (fieldName: keyof Errors) => {
    if (errors[fieldName]) {
      const newErrors = {
        ...errors
      };
      delete newErrors[fieldName];
      setErrors(newErrors);
    }
  };
  const onFirstNameChange = (event: React.ChangeEvent<InputElement>) => {
    setFirstName(event.target.value);
    clearError("firstName");
  };
  const onLastNameChange = (event: React.ChangeEvent<InputElement>) => {
    setLastName(event.target.value);
    clearError("lastName");
  };
  const onEmailChange = (event: React.ChangeEvent<InputElement>) => {
    setEmail(event.target.value);
    clearError("email");
  };
  const onPhoneChange = (newValue: string) => {
    setPhone(newValue);
    clearError("phone");
  };
  const isInputValid = (): boolean => {
    const newErrors: Errors = {};
    if (!firstName.trim()) newErrors.firstName = t("errors.required", "Required");
    if (!lastName.trim()) newErrors.lastName = t("errors.required", "Required");
    if (!email.trim()) {
      newErrors.email = t("errors.required", "Required");
    } else {
      const emailPattern = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
      if (!email.match(emailPattern)) {
        newErrors.email = t("errors.invalidEmail", "Invalid email address");
      }
    }
    if (!phone.trim() && isPhoneNumberRequiredForBookings) {
      newErrors.phone = t("errors.required", "Required");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!isInputValid() || !waitlistBooking) return;
    setSubmitError(null);
    try {
      const result = await joinWaitlist({
        variables: {
          input: {
            organisationId: organisation._id,
            serviceId: service._id,
            bookingId: waitlistBooking.bookingId,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim().toLowerCase(),
            phone: phone || undefined,
            timezone
          }
        }
      });
      if (result.data?.joinWaitlist?.success) {
        appDataDispatch({
          type: ActionType.COMPLETE_WAITLIST
        });
      } else {
        setSubmitError(result.data?.joinWaitlist?.message ?? t("waitlist.error", "Something went wrong. Please try again."));
      }
    } catch {
      setSubmitError(t("waitlist.error", "Something went wrong. Please try again."));
    }
  };
  const goBack = () => {
    appDataDispatch({
      type: ActionType.BACK_FROM_WAITLIST_STEP
    });
  };
  const eventDateDisplay = waitlistBooking ? new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: timezone
  }).format(waitlistBooking.startTime) : "";
  const eventTimeDisplay = waitlistBooking ? `${new Intl.DateTimeFormat(i18n.language, {
    timeStyle: "short",
    timeZone: timezone
  }).format(waitlistBooking.startTime)} - ${new Intl.DateTimeFormat(i18n.language, {
    timeStyle: "short",
    timeZone: timezone
  }).format(waitlistBooking.endTime)}` : "";
  return <>
      <Title data-sentry-element="Title" data-sentry-source-file="WaitlistStep.tsx">{t("waitlist.joinTitle", "Join the waitlist")}</Title>

      <EventInfo data-sentry-element="EventInfo" data-sentry-source-file="WaitlistStep.tsx">
        <EventName data-sentry-element="EventName" data-sentry-source-file="WaitlistStep.tsx">{waitlistBooking?.serviceName ?? service.name}</EventName>
        <EventDate data-sentry-element="EventDate" data-sentry-source-file="WaitlistStep.tsx">{eventDateDisplay}</EventDate>
        <EventTime data-sentry-element="EventTime" data-sentry-source-file="WaitlistStep.tsx">{eventTimeDisplay}</EventTime>
      </EventInfo>

      <FormTitle data-sentry-element="FormTitle" data-sentry-source-file="WaitlistStep.tsx">{t("yourDetails", "Your details")}</FormTitle>
      <Form onSubmit={e => {
      e.preventDefault();
      void handleSubmit();
    }} data-sentry-element="Form" data-sentry-source-file="WaitlistStep.tsx">
        <Input variant="text" id="waitlist-firstName" label={t("firstName", "First name")} error={errors.firstName} required value={firstName} onChange={onFirstNameChange} autoComplete="given-name" data-sentry-element="Input" data-sentry-source-file="WaitlistStep.tsx" />
        <Input variant="text" id="waitlist-lastName" label={t("lastName", "Last name")} error={errors.lastName} required value={lastName} onChange={onLastNameChange} autoComplete="family-name" data-sentry-element="Input" data-sentry-source-file="WaitlistStep.tsx" />
        <Input variant="email" id="waitlist-email" label={t("email", "Email")} error={errors.email} required value={email} onChange={onEmailChange} autoComplete="email" data-sentry-element="Input" data-sentry-source-file="WaitlistStep.tsx" />
        <Input variant="phone" id="waitlist-phone" label={t("phoneNumber", "Phone number")} error={errors.phone} required={isPhoneNumberRequiredForBookings} value={phone} onChange={onPhoneChange} data-sentry-element="Input" data-sentry-source-file="WaitlistStep.tsx" />
      </Form>

      {submitError && <ErrorMessage>{submitError}</ErrorMessage>}

      <NavButtons onBack={goBack} onContinue={() => {
      void handleSubmit();
    }} continueLabel={t("waitlist.joinButton", "Join waitlist")} navigationDisabled={loading} data-sentry-element="NavButtons" data-sentry-source-file="WaitlistStep.tsx" />
    </>;
}
const Title = styled.h2`
  ${typography.heading}
  ${marginBottom.m}
`;
const EventInfo = styled.div`
  ${marginBottom.l}
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: ${({
  theme
}) => theme.colours.dark ? `${theme.colours.dark}08` : "#f8f8f8"};
`;
const EventName = styled.div`
  ${typography.bodyBold}
  ${marginBottom.xs}
`;
const EventDate = styled.div`
  ${typography.body}
  &::first-letter {
    text-transform: capitalize;
  }
`;
const EventTime = styled.div`
  ${typography.body}
  color: ${({
  theme
}) => theme.colours.dark ? `${theme.colours.dark}99` : "#666"};
`;
const FormTitle = styled.h3`
  ${typography.bodyBold}
  ${marginBottom.m}
  &::first-letter {
    text-transform: capitalize;
  }
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
`;
const ErrorMessage = styled.div`
  ${typography.small}
  ${marginTop.m}
  color: ${({
  theme
}) => theme.colours.error ?? "#d32f2f"};
`;